import { useEffect, useRef, useState } from "react"

import { scrollBehavior } from "@/lib/helpers/scroll-behavior"

interface ReelPosition {
  index: number
  progress: number
  atStart: boolean
  atEnd: boolean
}

const INITIAL_POSITION: ReelPosition = { index: 0, progress: 0, atStart: true, atEnd: false }

const reachableStops = (element: HTMLElement, count: number) => {
  const max = element.scrollWidth - element.clientWidth
  const first = element.children[0]
  if (max <= 1 || !(first instanceof HTMLElement)) return count
  const starts = Array.from(element.children).filter((child) => child instanceof HTMLElement && child.offsetLeft - first.offsetLeft < max - 1).length
  return Math.min(count, starts + 1)
}

/* Pitch is measured from the first two children's offsetLefts (slide width +
   whatever gap the reel renders) so index math never hardcodes a gap; for
   w-full slides it degrades to clientWidth. */
export const useReelPagination = (count: number) => {
  const ref = useRef<HTMLElement>(null)
  const [position, setPosition] = useState<ReelPosition>(INITIAL_POSITION)
  const [overflowing, setOverflowing] = useState(false)
  const [stops, setStops] = useState(count)

  useEffect(() => {
    const element = ref.current
    if (!element) return
    let frame = 0
    const pitch = () => {
      const [first, second] = element.children
      return first instanceof HTMLElement && second instanceof HTMLElement ? second.offsetLeft - first.offsetLeft : element.clientWidth
    }
    const commit = () => {
      frame = 0
      const max = element.scrollWidth - element.clientWidth
      const last = reachableStops(element, count) - 1
      const atEnd = element.scrollLeft >= max - 1
      const next: ReelPosition = {
        index: max > 1 && atEnd ? last : Math.min(last, Math.round(element.scrollLeft / pitch())),
        progress: max > 0 ? Math.min(1, element.scrollLeft / max) : 0,
        atStart: element.scrollLeft <= 1,
        atEnd,
      }
      setPosition((previous) =>
        previous.index === next.index && previous.progress === next.progress && previous.atStart === next.atStart && previous.atEnd === next.atEnd ? previous : next
      )
    }
    const onScroll = () => {
      if (frame === 0) frame = requestAnimationFrame(commit)
    }
    const measure = () => {
      setOverflowing(element.scrollWidth > element.clientWidth + 1)
      setStops(reachableStops(element, count))
      commit()
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    const mutations = new MutationObserver(measure)
    mutations.observe(element, { childList: true })
    element.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      observer.disconnect()
      mutations.disconnect()
      element.removeEventListener("scroll", onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [count])

  // Scroll the reel only — scrollIntoView also scrolls ancestors vertically,
  // which yanks the page when the reel is half off-screen (gallery thumbs).
  const goTo = (target: number) => {
    const element = ref.current
    const child = element?.children[target]
    const first = element?.children[0]
    if (!element || !(child instanceof HTMLElement) || !(first instanceof HTMLElement)) return
    element.scrollTo({ left: child.offsetLeft - first.offsetLeft, behavior: scrollBehavior() })
  }
  const page = (direction: 1 | -1) => ref.current?.scrollBy({ left: direction * ref.current.clientWidth * 0.8, behavior: scrollBehavior() })

  return { ref, ...position, stops, overflowing, goTo, page }
}
